import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { API_URL } from "../../config";
import Notification from "../../components/Notification";
import { allowedToView } from "../../utils/PermissionUtils.js";

function CollectionRoutes() {
    const [collectionRoutes, setCollectionRoutes] = useState([]);
    const [newCollectionRoute, setNewCollectionRoute] = useState("");
    const [editCollectionRoute, setEditCollectionRoute] = useState(null);
    const [notification, setNotification] = useState({ type: "", message: "" });
    const [errors, setErrors] = useState({}); // State for input errors
    const navigate = useNavigate();
    const userRole = localStorage.getItem("userRole");
    const canEdit = (userRole) => {
        return (allowedToView(userRole, ['admin', 'superadmin']));
    };

    const token = localStorage.getItem("access_token");

    const fetchCollectionRoutes = useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/collection-routes`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
            });
            if (response.ok) {
                const data = await response.json();
                setCollectionRoutes(data);
            } else if (response.status === 401) {
                navigate("/logout");
            } else {
                throw new Error("Failed to fetch the collection routes");
            }
        } catch (error) {
            console.error("There was an error fetching the collection routes!", error);
            setNotification({ type: "danger", message: "Failed to fetch collection routes." });
        }
    }, [token, navigate]);

    useEffect(() => {
        fetchCollectionRoutes();
    }, [fetchCollectionRoutes]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setErrors({}); // Clear previous errors

        if (!newCollectionRoute.trim()) {
            setErrors({ name: ["Collection route name is required"] });
            return;
        }

        try {
            const response = editCollectionRoute
                ? await updateCollectionRoute(editCollectionRoute.id, newCollectionRoute)
                : await createCollectionRoute(newCollectionRoute);

            if (!response.ok) {
                const errorData = await response.json();
                setErrors(errorData || {});
                return;
            }

            const message = editCollectionRoute
                ? "Collection collection route updated successfully."
                : "Collection collection route added successfully.";

            setNotification({ type: "success", message });
            setNewCollectionRoute("");
            setEditCollectionRoute(null);
            fetchCollectionRoutes();
        } catch (error) {
            console.error("There was an error submitting the form!", error);
            setNotification({ type: "danger", message: "Failed to submit the collection route." });
        }
    };

    const createCollectionRoute = async (name) => {
        const response = await fetch(`${API_URL}/collection-routes`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ name }),
        });
        return response;
    };

    const updateCollectionRoute = async (id, name) => {
        const response = await fetch(`${API_URL}/collection-routes/${id}`, {
            method: "PUT",
            headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${token}`,
            },
            body: JSON.stringify({ name }),
        });
        return response;
    };

    const handleEdit = (collectionRoute) => {
        setNewCollectionRoute(collectionRoute.name);
        setEditCollectionRoute(collectionRoute);
    };

    const handleDelete = async (id) => {
        try {
            await fetch(`${API_URL}/collection-routes/${id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`,
                },
            });
            fetchCollectionRoutes();
            setNotification({ type: "success", message: "Collection route deleted successfully." });
        } catch (error) {
            console.error("There was an error deleting the collection route!", error);
            setNotification({ type: "danger", message: "Failed to delete the collection route." });
        }
    };

    const closeNotification = () => {
        setNotification({ type: "", message: "" });
    };

    return (
        <main>
            <nav className="level">
                <div className="level-left">
                    <div className="level-item">
                        <p className="title">Collection Routes</p>
                    </div>
                </div>
                <div className="level-right">

                </div>
            </nav>

            <div className="columns">
                <div className="column is-half dash-section vertical-separator">
                    <h1 className="section-title">Collection Routes</h1>
                    <table className="table is-striped is-fullwidth">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Options</th>
                            </tr>
                        </thead>
                        <tbody>
                            {collectionRoutes.map((collectionRoute, index) => (
                                <tr key={collectionRoute.id}>
                                    <td>{index + 1}</td>
                                    <td>{collectionRoute.name}</td>
                                    <td>
                                        {canEdit(userRole) && (
                                            <div className="dropdown is-right">
                                                <div className="dropdown-trigger">
                                                    <button
                                                        className="button is-white"
                                                        aria-haspopup="true"
                                                        aria-controls="dropdown-menu"
                                                        onClick={() => handleEdit(collectionRoute)}
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-edit"></i>
                                                        </span>
                                                    </button>
                                                    <button
                                                        className="button is-white"
                                                        aria-haspopup="true"
                                                        aria-controls="dropdown-menu"
                                                        onClick={() => handleDelete(collectionRoute.id)}
                                                    >
                                                        <span className="icon">
                                                            <i className="fas fa-trash-alt"></i>
                                                        </span>
                                                    </button>
                                                </div>
                                            </div>
                                        )}

                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {canEdit(userRole) && (
                    <div className="column is-half dash-section">
                        <h1 className="section-title">{editCollectionRoute ? "Edit Collection Route" : "Add New Collection Route"}</h1>

                        {notification.message && (
                            <Notification
                                type={notification.type}
                                message={notification.message}
                                onClose={closeNotification}
                            />
                        )}

                        <form onSubmit={handleSubmit}>
                            <div className="field">
                                <label className="label">Name</label>
                                <div className="control has-icons-left has-icons-right">
                                    <input
                                        className={`input ${errors.name ? "is-danger" : ""}`}
                                        type="text"
                                        placeholder="e.g Kamacharia-Kiriaini-Kamune"
                                        value={newCollectionRoute}
                                        onChange={(e) => setNewCollectionRoute(e.target.value)}
                                        required
                                    />
                                    <span className="icon is-small is-left">
                                        <i className="fas fa-school"></i>
                                    </span>
                                </div>
                                {errors.name && errors.name.map((error, index) => (
                                    <p key={index} className="help is-danger">{error}</p>
                                ))}
                            </div>

                            <div className="field">
                                <div className="control">
                                    <button type="submit" className="button primary-bg">{editCollectionRoute ? "Update" : "Add New"}</button>
                                </div>
                            </div>
                        </form>
                    </div>
                )}
            </div>
        </main>
    );
}

export default CollectionRoutes;
